import React, { useEffect } from 'react';

import { Elements, CardElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { useGroupState, useGroupActions } from './GroupContext';
import { createGroupSetupIntent, getGroupPaymentMethod, removeGroupPaymentMethod, updateGroupBillingAddress } from './restApi';
import Button from '../../Shared/UI/Button/components/Button';

import styles from './PermissionsSection.module.scss';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#fa755a' },
  },
};

const GroupCardForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const state = useGroupState();
  const { onResponse, onError, closeCardForm, setCardFormProcessing } = useGroupActions();

  useEffect(() => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    createGroupSetupIntent({
      id: state?.group?.id,
      onResponse,
      onError,
      body: { authenticityToken },
    });
  }, []);

  // The billing address is sent to Stripe twice, for two different purposes:
  //
  // 1. confirmCardSetup -- a client-side Stripe.js call that sends card details
  //    directly from the browser to Stripe (our server never sees the card number,
  //    which keeps us out of PCI scope). We pass billing_details.address here so
  //    Stripe can run AVS (Address Verification System) fraud checks against the
  //    card issuer's records. This attaches the address to the PaymentMethod.
  //
  // 2. updateGroupBillingAddress -- a call through our backend to persist the
  //    billing address on the Stripe Customer object. Stripe Tax reads the address
  //    from the Customer (not the PaymentMethod) to determine the tax jurisdiction,
  //    so this must be a separate request. The PaymentMethod address (for AVS/fraud)
  //    and Customer address (for tax) are stored separately by Stripe.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !state?.clientSecret) return;

    setCardFormProcessing(true);

    const addressElement = elements.getElement(AddressElement);
    const { complete, value: addressValue } = await addressElement.getValue();
    if (!complete) {
      onError({
        feedbackMessage: "Please complete the billing address.",
        feedbackVariant: "error",
      });
      setCardFormProcessing(false);
      return;
    }

    const result = await stripe.confirmCardSetup(state.clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: { address: addressValue.address },
      },
    });

    if (result.error) {
      onError({
        feedbackMessage: result.error.message,
        feedbackVariant: "error",
      });
      setCardFormProcessing(false);
    } else {
      const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
      const addr = addressValue.address;
      updateGroupBillingAddress({
        id: state?.group?.id,
        onResponse: () => {
          closeCardForm();
          getGroupPaymentMethod({ id: state?.group?.id, onResponse, onError });
          onResponse({
            feedbackMessage: "Payment method and billing address saved.",
            feedbackVariant: "success",
          });
        },
        onError: (data) => {
          closeCardForm();
          getGroupPaymentMethod({ id: state?.group?.id, onResponse, onError });
          onError(data);
        },
        body: {
          authenticityToken,
          line1: addr.line1,
          line2: addr.line2,
          city: addr.city,
          state: addr.state,
          postalCode: addr.postal_code,
          country: addr.country,
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '16px',
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontWeight: 500, marginBottom: '8px', fontSize: '14px' }}>Billing Address</p>
        <AddressElement options={{ mode: 'billing' }} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          variant="primary"
          type="submit"
          disabled={!stripe || state?.cardFormProcessing || !state?.clientSecret}
        >
          {state?.cardFormProcessing ? 'Saving...' : 'Save Payment Method'}
        </Button>
        <Button variant="ghost" onClick={closeCardForm}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const GroupBillingSection = () => {
  const state = useGroupState();
  const { onResponse, onError, openCardForm } = useGroupActions();

  const groupId = state?.group?.id;

  useEffect(() => {
    if (groupId) {
      getGroupPaymentMethod({ id: groupId, onResponse, onError });
    }
  }, [groupId]);

  const handleRemove = () => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    removeGroupPaymentMethod({
      id: groupId,
      onResponse,
      onError,
      body: { authenticityToken },
    });
  };

  const paymentMethod = state?.groupPaymentMethod;

  const renderPaymentMethodContent = () => {
    if (state?.cardFormIsOpen && state?.stripePromise) {
      return (
        <Elements stripe={state.stripePromise}>
          <GroupCardForm />
        </Elements>
      );
    }

    if (paymentMethod) {
      const brandDisplay = paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1);
      return (
        <>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
            {brandDisplay} ending in {paymentMethod.last4}, expires {paymentMethod.expMonth}/{paymentMethod.expYear}
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" onClick={openCardForm}>
              Update
            </Button>
            <Button variant="ghost" onClick={handleRemove}>
              Remove
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <p className={styles.coveredNote}>No payment method on file for this group.</p>
        <Button
          variant="primary"
          onClick={openCardForm}
          disabled={!state?.stripePromise}
          style={{ marginTop: '8px' }}
        >
          Add Payment Method
        </Button>
      </>
    );
  };

  const taxExempt = state?.taxExempt;
  const usageType = state?.usageType || 'business';

  const handleUsageTypeChange = (newUsageType) => {
    if (!groupId || !state?.groupPaymentMethod) return;
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    updateGroupBillingAddress({
      id: groupId,
      onResponse,
      onError,
      body: {
        authenticityToken,
        usageType: newUsageType,
      },
    });
  };

  const renderTaxExemptBanner = () => {
    if (taxExempt) {
      return (
        <div style={{
          background: '#e8f5e9',
          border: '1px solid #a5d6a7',
          borderRadius: '6px',
          padding: '12px 16px',
          marginTop: '12px',
          fontSize: '14px',
        }}>
          <p style={{ fontWeight: 600, margin: 0 }}>
            Tax-exempt status: Verified
          </p>
          <p style={{ margin: '4px 0 0 0' }}>
            Your organization is verified as tax-exempt. Sales tax will not be applied to subscriptions.
          </p>
        </div>
      );
    }

    return (
      <div style={{
        background: '#fef9e7',
        border: '1px solid #f0e3a0',
        borderRadius: '6px',
        padding: '12px 16px',
        marginTop: '12px',
        fontSize: '14px',
      }}>
        <p style={{ fontWeight: 600, marginBottom: '4px' }}>
          Tax-exempt organization?
        </p>
        <p style={{ margin: 0 }}>
          If your organization is tax-exempt, please email{' '}
          <a href="mailto:contact@isibute.com">contact@isibute.com</a>{' '}
          before adding a payment method. We will verify your exemption status
          and confirm via email. If you add a payment method or subscribe
          members before receiving confirmation, sales tax may be applied to
          your charges.
        </p>
      </div>
    );
  };

  const membershipBilling = state?.membershipBilling;

  const renderMembershipBilling = () => {
    if (!membershipBilling) return null;

    const { membersCount, freeLimit, billableCount, monthlyCostDollars, subscriptionStatus } = membershipBilling;

    const statusLabel = {
      active: 'Active',
      pending_cancellation: 'Canceling at end of period',
      canceled: 'Canceled',
    }[subscriptionStatus] || 'None';

    return (
      <div className={styles.adminsSubsection} style={{ marginTop: '16px' }}>
        <span className={styles.subsectionLabel}>Membership billing:</span>
        <div style={{ marginTop: '4px', fontSize: '14px' }}>
          <p style={{ margin: '0 0 4px 0' }}>
            <strong>{membersCount}</strong> total member{membersCount !== 1 ? 's' : ''}
            {' '}&middot;{' '}
            first {freeLimit} free
            {billableCount > 0 && (
              <> &middot; <strong>{billableCount}</strong> billable</>
            )}
          </p>
          {billableCount > 0 ? (
            <p style={{ margin: '0 0 4px 0' }}>
              Monthly cost: <strong>${monthlyCostDollars.toFixed(2)}</strong>/month
              {' '}({billableCount} &times; $1.00)
            </p>
          ) : (
            <p style={{ margin: '0 0 4px 0', color: '#666' }}>
              No membership charges. Groups with {freeLimit} or fewer members are free.
            </p>
          )}
          {subscriptionStatus && (
            <p style={{ margin: '0', color: '#666' }}>
              Subscription status: {statusLabel}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.adminsSection}>
      <h3 className={styles.adminsSectionTitle}>Billing</h3>
      <p className={styles.adminsSectionDescription}>
        Add a payment method to subscribe group members to Premium Job Recommendations ($19.99/member/month + applicable tax).
        Groups with more than 10 members are billed $1.00/member/month for each member above 10.
      </p>

      <div className={styles.adminsSubsection}>
        <span className={styles.subsectionLabel}>Payment method:</span>
        {renderPaymentMethodContent()}
      </div>

      {renderMembershipBilling()}

      {renderTaxExemptBanner()}

      {paymentMethod && (
        <div className={styles.adminsSubsection} style={{ marginTop: '16px' }}>
          <span className={styles.subsectionLabel}>Usage type:</span>
          <div style={{ marginTop: '4px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
              <input
                type="radio"
                name="groupUsageType"
                value="personal"
                checked={usageType === 'personal'}
                onChange={() => handleUsageTypeChange('personal')}
              />
              {' '}Personal use
            </label>
            <label style={{ display: 'block', fontSize: '14px' }}>
              <input
                type="radio"
                name="groupUsageType"
                value="business"
                checked={usageType === 'business'}
                onChange={() => handleUsageTypeChange('business')}
              />
              {' '}Business use
            </label>
            <p style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
              This determines how sales tax is calculated for member subscriptions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupBillingSection;
